import React, { FormEventHandler, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useControls } from 'src/components/async/model/control/useControls'
import { FrameworkSectionsEditor } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor'
import {
  FrameworkSectionsEditorErrors,
  SectionDraft,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.types'
import {
  DuplicateSectionNameError,
  useSaveFrameworkSections,
} from 'src/pages/Frameworks/frameworks.hooks'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import type { SectionEditSelection } from 'src/pages/Frameworks/show/frameworksShowSections.types'
import { ModalForm, ModalFormId } from '../..'

type Props = {
  framework: ComplianceFramework
  selectedSection: SectionEditSelection | null
  clearSelectedSection: () => void
}

export const EditFrameworkSectionModal = ({
  framework,
  selectedSection,
  clearSelectedSection,
}: Props): JSX.Element => {
  const show = useIsShowModal(ModalFormId.EDIT_FRAMEWORK_SECTION)
  const hideModal = useHideModal({
    modalId: ModalFormId.EDIT_FRAMEWORK_SECTION,
  })
  const saveSections = useSaveFrameworkSections()
  const cfModelId = framework.getModelId()
  const { data: controlsData, isLoading: controlsLoading } = useControls(
    new ListControlsRequest(),
    show,
  )
  const [sectionDraft, setSectionDraft] = useState<SectionDraft[]>([])
  const [sectionNameErrors, setSectionNameErrors] = useState<
    Record<string, string>
  >({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!show || !selectedSection) return
    setSectionDraft([selectedSection.sectionDraft])
    setSectionNameErrors({})
    setIsSubmitting(false)
  }, [selectedSection, show])

  const controlOptions = useMemo(
    () =>
      (controlsData?.getItemsList() ?? []).map((control) => ({
        name: `${control.getModelId()} ${control.getName()}`,
        value: control.getModelId(),
      })),
    [controlsData],
  )

  const errors: FrameworkSectionsEditorErrors = useMemo(
    () => ({
      sectionNames: sectionNameErrors,
    }),
    [sectionNameErrors],
  )

  const closeAndReset = () => {
    hideModal()
    setSectionDraft([])
    setSectionNameErrors({})
    setIsSubmitting(false)
    clearSelectedSection()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (!selectedSection || sectionDraft.length === 0) return
    setSectionNameErrors({})
    setIsSubmitting(true)
    try {
      await saveSections({
        complianceFrameworkModelId: cfModelId,
        originalSections: selectedSection.sectionDraftsSnapshot,
        newSections: selectedSection.sectionDraftsSnapshot.map(
          (snapshotSection) =>
            snapshotSection.tempId === selectedSection.sectionId
              ? sectionDraft[0]
              : snapshotSection,
        ),
        controls: controlsData?.getItemsList() ?? [],
      })
      toast.success('Section updated.')
      closeAndReset()
    } catch (err) {
      if (err instanceof DuplicateSectionNameError) {
        setSectionNameErrors({
          [sectionDraft[0].tempId]: 'Section name already exists.',
        })
      }
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('EditFrameworkSectionModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.EDIT_FRAMEWORK_SECTION}
      size="xl"
      title="Edit Section"
      submitText="Save"
      showOverflow
      disableSubmitButton={
        isSubmitting ||
        controlsLoading ||
        !selectedSection ||
        sectionDraft.length === 0
      }
    >
      <form id={ModalFormId.EDIT_FRAMEWORK_SECTION} onSubmit={onSubmit}>
        <FrameworkSectionsEditor
          sections={sectionDraft}
          setSections={setSectionDraft}
          controlOptions={controlOptions}
          errors={errors}
          disableSectionCrud
        />
      </form>
    </ModalForm>
  )
}
